import { useQuery } from "@apollo/client"
import { Link } from "react-router-dom"
import invariant from "tiny-invariant"
import { gql } from "~/__generated__"
import { EmailPreference } from "~/__generated__/graphql"
import { profileEditPath, todoPath } from "~/common/paths"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Switch } from "~/components/switch"
import pencilIcon from "~/images/pencil-icon"
import { LoadingSpinner } from "~/ui/loading-spinner"
import { UserAvatar } from "~/users/user-avatar"

type EmailPreferenceSwitchProps = {
  title: string
  description: string
  field: string
  checked: boolean
  onToggle: (field: string) => (checked: boolean) => Promise<void>
}

const EmailPreferenceSwitch = ({
  title,
  description,
  field,
  checked,
  onToggle,
}: EmailPreferenceSwitchProps) => (
  <button
    type="button"
    className="flex w-full items-center justify-between"
    onClick={() => onToggle(field)(!checked)}
  >
    <div>
      <div className="text-left font-medium">{title}</div>
      <div className="text-left text-sm text-gray-500">{description}</div>
    </div>
    <Switch checked={checked} onCheckedChange={onToggle(field)} />
  </button>
)

const GET_VIEWER = gql(/* GraphQL */ `
  query GetViewer {
    viewer {
      id
      fullName
      email
      newGroupBookmarkEmail
      newGroupMemberEmail
      weeklyDigest
      ...UserAvatar
    }
  }
`)

const UPDATE_USER = gql(/* GraphQL */ `
  mutation UpdateUser($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      user {
        id
        newGroupBookmarkEmail
        newGroupMemberEmail
        weeklyDigest
      }
    }
  }
`)

export const ProfileScreen = () => {
  const { data } = useQuery(GET_VIEWER)
  const viewer = data?.viewer
  const [updateUser] = useSafeMutation(UPDATE_USER)

  const handleToggle = (field: string) => async (checked: boolean) => {
    invariant(viewer)
    await updateUser({
      variables: {
        input: {
          id: viewer.id,
          userInput: {
            [field]: checked ? EmailPreference.On : EmailPreference.Off,
          },
        },
      },
    })
  }

  const isEnabled = (pref: EmailPreference) => pref !== EmailPreference.Off

  if (!viewer) return <LoadingSpinner />

  return (
    <div className="flex flex-1 flex-col">
      <div className="bottom-1 flex items-center justify-between border-b border-gray-300 py-4">
        <h1 className="text-2xl">My Profile</h1>
      </div>
      <div className="mb-4 flex items-center space-x-6 border-b border-gray-300 py-8">
        <div className="flex-none">
          <UserAvatar
            user={viewer}
            alt="A picture that represents you and your account"
            className="h-16 w-16"
          />
        </div>
        <div className="flex-1 font-medium">
          <div className="text-lg">{viewer.fullName}</div>
          <div className="text-xs text-gray-400">{viewer.email}</div>
        </div>
        <div className="flex-none">
          <Link className="text-xs text-gray-400" to={profileEditPath({})}>
            <img {...pencilIcon} className="mr-2 inline-flex" />
            Edit Details
          </Link>
        </div>
      </div>
      <div className="mb-8 border-b border-gray-300 py-4">
        <h2 className="mb-4 text-lg font-medium">Email Settings</h2>
        <div className="space-y-4">
          <EmailPreferenceSwitch
            title="New Group Bookmarks"
            description="Get notified when bookmarks are added to your groups"
            field="newGroupBookmarkEmail"
            checked={isEnabled(viewer.newGroupBookmarkEmail)}
            onToggle={handleToggle}
          />
          <EmailPreferenceSwitch
            title="New Group Members"
            description="Get notified when new members join your groups"
            field="newGroupMemberEmail"
            checked={isEnabled(viewer.newGroupMemberEmail)}
            onToggle={handleToggle}
          />
          <EmailPreferenceSwitch
            title="Weekly Digest"
            description="Get a weekly summary of activity in your groups"
            field="weeklyDigest"
            checked={isEnabled(viewer.weeklyDigest)}
            onToggle={handleToggle}
          />
        </div>
      </div>
      <div className="py-4">
        <Link className="text-xs text-gray-400" to={todoPath({})}>
          Delete Account
        </Link>
      </div>
    </div>
  )
}
